import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { addMonths } from 'date-fns';
import { BehaviorSubject, Observable, catchError, map, of, take } from 'rxjs';
import { InitialConfigService } from '../app-config/initial-config.service';
import { CStorageKeys } from '../storage/storage-keys.const';
import { StorageService } from '../storage/storage.service';
import { IConsent, ICookieConsentDTO } from './consent.model';

@Injectable({
  providedIn: 'root'
})
export class ConsentService {
  private currentConsent$: BehaviorSubject<IConsent | undefined> = new BehaviorSubject<IConsent | undefined>(undefined);
  readonly expiryMonths = 3;

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private initialConfigService: InitialConfigService
  ) {}

  /**
   * On initial load checks for memory (only if passed in URL), then local storage for existing consent item.
   * @returns the consent item or undefined to trigger a modal.
   */
  initConsent(venueSlug: string): Observable<IConsent | undefined> {
    return this.getCurrentConsent().pipe(
      take(1),
      map((consent: IConsent | undefined) => {
        if (consent) {
          if (consent.venueSlug === '') {
            consent.venueSlug = venueSlug;
            this.setAndStoreCurrentConsent(consent);
          }

          return consent;
        }

        const coldConsents = this.storageService.getItem<IConsent[]>(CStorageKeys.consent);
        if (!coldConsents || typeof coldConsents === 'string') {
          return undefined;
        }

        const coldVenueSpecificConsent = coldConsents.find((consent: IConsent) => consent.venueSlug === venueSlug);
        if (coldVenueSpecificConsent) {
          return new Date().getTime() <
            addMonths(new Date(coldVenueSpecificConsent.lastUpdated), this.expiryMonths).getTime()
            ? coldVenueSpecificConsent
            : undefined;
        }

        return undefined;
      })
    );
  }

  /**
   * Gets the current subject containing the currentConsent. Only emits when it has value.
   * @returns Observable of the currentConsent items.
   */
  getCurrentConsent(): Observable<IConsent | undefined> {
    return this.currentConsent$.asObservable();
  }

  /**
   * Updates the stored consent object in local storage.
   * @param value object of IConsent
   */
  setAndStoreCurrentConsent(value: IConsent): void {
    let coldConsents = this.storageService.getItem<IConsent[]>(CStorageKeys.consent);
    if (!coldConsents || typeof coldConsents === 'string') {
      coldConsents = [value];
    } else {
      coldConsents = coldConsents.filter((consent: IConsent) => consent.venueSlug !== value.venueSlug).concat([value]);
    }

    this.storageService.setItem(CStorageKeys.consent, JSON.stringify(coldConsents));
    this.setCurrentConsent(value);
  }

  /** Updates the subject with a new consent object. */
  setCurrentConsent(value: IConsent): void {
    this.currentConsent$.next(value);
  }

  /** Creates the default consent with marketing and performance off. */
  createDefaultConsent(venueSlug: string, accountId?: number): IConsent {
    return {
      userId: accountId?.toString(),
      venueSlug,
      lastUpdated: new Date(),
      consent: {
        isMarketingGranted: false,
        isPerformanceGranted: false
      }
    };
  }

  /** If consent is not required, assumed all is granted, not stored so slug and date are not relevant. */
  createConsentNotRequired(): IConsent {
    return {
      venueSlug: '',
      lastUpdated: new Date(),
      consent: {
        isMarketingGranted: true,
        isPerformanceGranted: true
      }
    };
  }

  /** On update change the date and store in local storage as well as memory. */
  updateConsent(consentItem: IConsent): IConsent {
    consentItem.lastUpdated = new Date();

    this.setAndStoreCurrentConsent(consentItem);
    return consentItem;
  }

  /** If a user is logged in and they modify their cookie consent, posts to api to log in the activity stream */
  postCookieConsent(consentItem: IConsent | undefined, checkoutUrl: string): Observable<boolean> {
    const payload: ICookieConsentDTO | undefined = this.createConsentPayload(consentItem, checkoutUrl);
    if (!consentItem || !payload) {
      return of(false);
    }

    return this.http
      .post(`${this.initialConfigService.getBaseApiUrl()}/checkout/user/details/cookie/consent`, payload)
      .pipe(
        map(() => {
          return true;
        }),
        catchError(() => {
          return of(false);
        })
      );
  }

  /** Creates consent dto from the local consentItem and the checkoutUrl */
  createConsentPayload(consentItem: IConsent | undefined, checkoutUrl: string): ICookieConsentDTO | undefined {
    if (!consentItem) {
      return undefined;
    }

    return {
      checkoutUrl,
      isMarketingGranted: consentItem.consent.isMarketingGranted,
      isPerformanceGranted: consentItem.consent.isPerformanceGranted,
      parentUrl: consentItem.consent.parentUrl
    };
  }
}
